import { Box, CardActionArea, Chip, Stack } from "@mui/material";
import Card from "@mui/material/Card";
import CardMedia from "@mui/material/CardMedia";
import { Icon } from "../../components/Icon";
import { P } from "../../components/Typography";
import { gray500, gray900, primary500 } from "../../theme";
import { useGamification } from "../Gamification/GamificationProvider";
import { defineMessages } from "react-intl";
import { useMsg } from "../../components/Msg/Msg";
import {
  append,
  curryN,
  flip,
  gt,
  join,
  pipe,
  propSatisfies,
  take,
  when,
} from "ramda";

export const RESOURCE_TYPE = {
  VIDEO: "Video",
  ARTICLE: "Article",
  TUTORIAL: "Tutorial",
};

const resourceMessages = defineMessages({
  "dict.resources.video.name": {
    id: "dict.resources.video.name",
    defaultMessage: "Video",
  },
  "dict.resources.article.name": {
    id: "dict.resources.article.name",
    defaultMessage: "Article",
  },
  "dict.resources.tutorial.name": {
    id: "dict.resources.tutorial.name",
    defaultMessage: "Tutorial",
  },
  "dict.resources.video.action": {
    id: "dict.resources.video.action",
    defaultMessage: "watch",
  },
  "dict.resources.article.action": {
    id: "dict.resources.article.action",
    defaultMessage: "read",
  },
  "dict.resources.tutorial.action": {
    id: "dict.resources.tutorial.action",
    defaultMessage: "",
  },
});

export const useTranslateResource = (type) => {
  const msg = useMsg({ dict: resourceMessages });

  return {
    name: msg(`dict.resources.${type.toLowerCase()}.name`) || type,
    action: msg(`dict.resources.${type.toLowerCase()}.action`) || "",
  };
};

const RESOURCE_CONFIG = {
  [RESOURCE_TYPE.VIDEO]: {
    color: "#056AD6",
    bgcolor: "#EFF9FF",
    iconName: "PlayCircleOutline",
  },
  [RESOURCE_TYPE.ARTICLE]: {
    color: primary500,
    bgcolor: "#F9F8FF",
    iconName: "MenuBookOutlined",
  },
  [RESOURCE_TYPE.TUTORIAL]: { color: "#876205", bgcolor: "#FFFBE1" },
  default: { color: "#056AD6", bgcolor: "#EFF9FF" },
};

export function getBase64ImageSrc(rawBase64) {
  if (!rawBase64) return null;
  if (rawBase64.startsWith("iVBOR")) {
    // PNG
    return `data:image/png;base64,${rawBase64}`;
  }
  if (rawBase64.startsWith("/9j/")) {
    // JPEG
    return `data:image/jpeg;base64,${rawBase64}`;
  }
  if (rawBase64.startsWith("PHN2Zy")) {
    // SVG (base64-encoded '<svg')
    return `data:image/svg+xml;base64,${rawBase64}`;
  }
  return rawBase64;
}

export const ResourceInfo = ({ resourceType, duration }) => {
  const { iconName } =
    RESOURCE_CONFIG[resourceType] || RESOURCE_CONFIG["default"];
  const { action } = useTranslateResource(resourceType);

  return (
    <P
      sx={{
        fontSize: 12,
        color: gray500,
        fontWeight: 400,
        display: "inline-flex",
        flexDirection: "row",
        alignItems: "center",
      }}
    >
      <Icon
        name={iconName}
        sx={{ color: "inherit", fontSize: "inherit", mr: 0.5 }}
      />
      <span>
        {duration} {action}
      </span>
    </P>
  );
};

export const ResourceChip = ({ resourceType }) => {
  const { color, bgcolor } =
    RESOURCE_CONFIG[resourceType] || RESOURCE_CONFIG["default"];
  const { name } = useTranslateResource(resourceType);

  return (
    <Chip
      label={name}
      size="small"
      sx={{
        borderRadius: "6px",
        p: "4px 0px 4px 0px",
        height: "auto",
        fontSize: 12,
        color,
        bgcolor,
        fontWeight: 400,
      }}
    />
  );
};

const truncate = curryN(2, (len, str) =>
  when(
    propSatisfies(flip(gt)(len), "length"),
    pipe(take(len - 1), append("…"), join("")),
    str,
  ),
);

const PEREX_MAX_LENGTH = 750;

export const ResourceMediaCard = ({
  title,
  perex,
  href,
  externalHref = true,
  thumbnail,
  rawBase64,
  type = RESOURCE_TYPE.VIDEO,
  length,
  sx = {},
  onClick: onClickProp,
}) => {
  const image = thumbnail
    ? thumbnail
    : rawBase64
      ? getBase64ImageSrc(rawBase64)
      : undefined;
  const { handleVideoWatched } = useGamification();
  const onClick = (e) => {
    if (type === RESOURCE_TYPE.VIDEO) {
      handleVideoWatched();
    }
    if (onClickProp) {
      onClickProp(e);
    }
  };
  const hrefProps = externalHref
    ? { href, target: "_blank", rel: "noopener noreferrer" }
    : { href };

  return (
    <Card sx={sx}>
      <CardActionArea
        onClick={onClick}
        {...hrefProps}
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {!image ? null : (
          <CardMedia
            sx={{ height: 130, width: "100%" }}
            image={image}
            title="preview"
            // component="img"
          />
        )}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: 2,
            p: 2,
            pb: 2,
            width: "100%",
          }}
        >
          <P sx={{ fontWeight: 500, color: gray900 }}>{title}</P>
          {perex && <P>{truncate(PEREX_MAX_LENGTH, perex)}</P>}
          <Stack
            direction="row"
            sx={{ alignItems: "center", justifyContent: "space-between" }}
          >
            <ResourceInfo resourceType={type} duration={length} />
            <ResourceChip resourceType={type} />
          </Stack>
        </Box>
        {/* <CardActions>
        <Button size="small">Share</Button>
        <Button size="small">Learn More</Button>
      </CardActions> */}
      </CardActionArea>
    </Card>
  );
};

ResourceMediaCard.PEREX_MAX_LENGTH = PEREX_MAX_LENGTH;
